import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  LeaderboardPublicPort,
  type LoadMagicLinkPreflightParams,
  type LoadPublicLeaderboardParams,
} from '../../../domain/leaderboard/ports/leaderboard-public.port';
import type { PublicLeaderboardLoadOutcome } from '../../../domain/leaderboard/models/leaderboard-unavailable.model';
import type { MagicLinkPreflightOutcome } from '../../../domain/leaderboard/models/magic-link-preflight.model';
import type {
  LeaderboardEventsDataResponse,
  PublicOverviewApiData,
  PublicParticipantApiData,
  PublicRankingListApiData,
} from '../dtos/leaderboard-public-api.dto';
import { mapOverviewApiToDomain } from '../mappers/map-overview-api-to-domain';
import { mapRankingItemsFromApi } from '../mappers/map-ranking-api-to-domain';
import { mapParticipantApiToSelfRow } from '../mappers/map-participant-api-to-domain';
import { mapParticipantApiToSnapshot } from '../mappers/map-participant-api-to-snapshot';
import { composePublicLeaderboard } from '../mappers/compose-public-leaderboard';
import { mapPublicLeaderboardHttpError } from '../utils/map-public-leaderboard-http-error';
import { leaderboardBoardBaseUrl } from '../utils/leaderboard-events-http.util';

const DEFAULT_RANKING_LIMIT = 50;

@Injectable({ providedIn: 'root' })
export class LeaderboardPublicHttpAdapter extends LeaderboardPublicPort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  loadPublic(params: LoadPublicLeaderboardParams): Observable<PublicLeaderboardLoadOutcome> {
    const base = leaderboardBoardBaseUrl(params.commerceId, params.leaderboardId);
    if (!base) {
      return of({ status: 'unavailable', reason: 'missing_params' });
    }

    let rankingParams = new HttpParams()
      .set('page', String(params.rankingPage ?? 1))
      .set('limit', String(params.rankingLimit ?? DEFAULT_RANKING_LIMIT));
    const q = params.rankingQuery?.trim();
    if (q) {
      rankingParams = rankingParams.set('q', q);
    }

    const overview$ = this.http.get<LeaderboardEventsDataResponse<PublicOverviewApiData>>(
      `${base}/overview`,
    );
    const ranking$ = this.http.get<LeaderboardEventsDataResponse<PublicRankingListApiData>>(
      `${base}/ranking`,
      { params: rankingParams },
    );

    const uid = params.uid?.trim();
    const participant$ = uid
      ? this.http
          .get<LeaderboardEventsDataResponse<PublicParticipantApiData>>(
            `${base}/participant`,
            { params: new HttpParams().set('uid', uid) },
          )
          .pipe(catchError(() => of(null)))
      : of(null);

    return forkJoin({ overview: overview$, ranking: ranking$, participant: participant$ }).pipe(
      map(({ overview: overviewRes, ranking: rankingRes, participant: participantRes }) => {
        const overview = mapOverviewApiToDomain(overviewRes?.data);
        if (!overview) {
          return { status: 'unavailable' as const, reason: 'not_found' as const };
        }
        const progressCtx = {
          activityType: overview.activityType,
          leaderXp: 0,
          xpWinThreshold: overview.summary.xpWinThreshold,
        };
        const ranking = mapRankingItemsFromApi(rankingRes?.data?.items, progressCtx);
        if (!ranking) {
          return { status: 'unavailable' as const, reason: 'not_found' as const };
        }
        progressCtx.leaderXp = ranking.ranking.find((r) => r.rank === 1)?.xp ?? 0;
        const selfRow = mapParticipantApiToSelfRow(participantRes?.data ?? undefined, progressCtx);
        const board = composePublicLeaderboard(overview, ranking, selfRow);
        return { status: 'ok' as const, board };
      }),
      catchError((err) =>
        of({
          status: 'unavailable' as const,
          reason: mapPublicLeaderboardHttpError(err),
        }),
      ),
    );
  }

  loadMagicLinkPreflight(params: LoadMagicLinkPreflightParams): Observable<MagicLinkPreflightOutcome> {
    const base = leaderboardBoardBaseUrl(params.commerceId, params.leaderboardId);
    const uid = params.uid?.trim();
    if (!base || !uid) {
      return of({ status: 'unavailable', reason: 'missing_params' });
    }

    const overview$ = this.http.get<LeaderboardEventsDataResponse<PublicOverviewApiData>>(
      `${base}/overview`,
    );
    const participant$ = this.http
      .get<LeaderboardEventsDataResponse<PublicParticipantApiData>>(`${base}/participant`, {
        params: new HttpParams().set('uid', uid),
      })
      .pipe(catchError(() => of(null)));

    return forkJoin({ overview: overview$, participant: participant$ }).pipe(
      map(({ overview: overviewRes, participant: participantRes }) => {
        const overview = mapOverviewApiToDomain(overviewRes?.data);
        if (!overview) {
          return { status: 'unavailable' as const, reason: 'not_found' as const };
        }
        return {
          status: 'ok' as const,
          termsAndConditionsUrl: overview.termsAndConditionsUrl,
          participant: mapParticipantApiToSnapshot(participantRes?.data ?? undefined),
          layoutPreview: {
            title: overview.title,
            branding: overview.branding,
            logoUrl: overview.logoUrl,
          },
        };
      }),
      catchError((err) =>
        of({
          status: 'unavailable' as const,
          reason: mapPublicLeaderboardHttpError(err),
        }),
      ),
    );
  }
}
